import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Store,
  CloudOff,
  UserX,
  ClipboardList,
  ShoppingBag,
  Bell,
  LineChart,
  ArrowRight,
  Sparkles,
} from "lucide-react";

export default function BeforeAfter({ onCta }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(6, Math.min(94, x)));
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="relative py-24 lg:py-32 overflow-hidden border-y border-white/5"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[70%] radial-glow-cyan blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold">
            Before vs After
          </p>
          <h2
            data-testid="before-after-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            Drag the slider. See the{" "}
            <span className="text-gradient-neon">transformation</span>.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Move the handle left and right to compare the offline struggle with
            the online momentum your business deserves.
          </p>
        </div>

        <div
          ref={containerRef}
          data-testid="before-after-slider"
          onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
          onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
          className="relative mx-auto max-w-5xl h-[460px] sm:h-[500px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-violet-900/40 select-none cursor-col-resize"
          style={{ touchAction: "none" }}
        >
          {/* AFTER (base, full width) */}
          <div
            data-testid="before-after-after"
            className="absolute inset-0 bg-gradient-to-br from-[#0a0f2e] via-[#0c0820] to-[#120428]"
          >
            <div className="absolute inset-0 radial-glow-violet blur-2xl opacity-70" />
            <div className="relative h-full p-6 sm:p-10 flex flex-col">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-[10px] uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" /> After — Online
                </span>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">
                  digital-onboarding · live
                </span>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  { label: "Orders", value: "128", tone: "text-emerald-300" },
                  { label: "Revenue", value: "₹54K", tone: "text-blue-300" },
                  { label: "Rating", value: "4.9★", tone: "text-pink-300" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                    <p className={`mt-1 font-display text-xl sm:text-2xl font-bold ${s.tone}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-xl bg-white/[0.03] border border-white/10 p-4 flex items-end gap-1.5 h-32 sm:h-36">
                {[18, 28, 22, 40, 52, 48, 64, 75, 82, 95].map((h, i) => (
                  <motion.span
                    key={i}
                    initial={{ height: 0 }}
                    whileInView={{ height: `${h}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.05 }}
                    className="flex-1 rounded-sm bg-gradient-to-t from-blue-500 via-violet-500 to-pink-400"
                  />
                ))}
              </div>

              <div className="mt-4 space-y-2">
                {[
                  { icon: Bell, text: "New order from Riya · ₹349", time: "1m" },
                  { icon: ShoppingBag, text: "3 items added to cart", time: "4m" },
                  { icon: LineChart, text: "Today's revenue ↑ 34%", time: "12m" },
                ].map((n, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
                    className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2"
                  >
                    <span className="w-8 h-8 rounded-lg btn-primary-grad flex items-center justify-center">
                      <n.icon className="w-4 h-4 text-white" />
                    </span>
                    <p className="text-sm text-slate-200 flex-1">{n.text}</p>
                    <span className="text-[10px] text-slate-500">{n.time}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* BEFORE (clipped by pos) */}
          <div
            data-testid="before-after-before"
            className="absolute inset-0 bg-[#0a0a12]"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative h-full p-6 sm:p-10 flex flex-col text-slate-500">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200 text-[10px] uppercase tracking-widest">
                  <CloudOff className="w-3 h-3" /> Before — Offline
                </span>
                <span className="text-[10px] uppercase tracking-widest text-slate-600">
                  paper · manual
                </span>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 opacity-70">
                {[
                  { label: "Walk-ins", value: "4" },
                  { label: "Revenue", value: "₹2K" },
                  { label: "Reach", value: "Local" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-slate-600">{s.label}</p>
                    <p className="mt-1 font-display text-xl sm:text-2xl font-bold text-slate-400">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-xl bg-white/[0.02] border border-white/5 p-4 h-32 sm:h-36 flex items-end gap-1.5">
                {[10, 12, 8, 14, 9, 11, 7, 10, 8, 12].map((h, i) => (
                  <span
                    key={i}
                    style={{ height: `${h}%` }}
                    className="flex-1 rounded-sm bg-slate-700/70"
                  />
                ))}
              </div>

              <div className="mt-4 space-y-2 opacity-80">
                {[
                  { icon: UserX, text: "No new customer today", time: "—" },
                  { icon: ClipboardList, text: "Manual register kharab", time: "—" },
                  { icon: Store, text: "Phir se khaali dukaan", time: "—" },
                ].map((n, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl bg-white/[0.02] border border-white/5 px-3 py-2"
                  >
                    <span className="w-8 h-8 rounded-lg bg-slate-800/80 border border-white/5 flex items-center justify-center">
                      <n.icon className="w-4 h-4 text-slate-500" />
                    </span>
                    <p className="text-sm text-slate-500 flex-1">{n.text}</p>
                    <span className="text-[10px] text-slate-700">{n.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Divider handle */}
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-white/80 to-transparent pointer-events-none"
            style={{ left: `${pos}%` }}
          />
          <button
            data-testid="before-after-handle"
            aria-label="Drag to compare"
            onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
            onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full btn-primary-grad flex items-center justify-center shadow-2xl shadow-violet-500/50 cursor-grab active:cursor-grabbing"
            style={{ left: `${pos}%` }}
          >
            <div className="flex items-center gap-0.5 text-white">
              <span className="block w-1 h-1 rounded-full bg-white" />
              <span className="block w-1 h-1 rounded-full bg-white" />
            </div>
          </button>

          {/* Arrow hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-slate-400 flex items-center gap-2 bg-black/50 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
            ← Drag to compare →
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={onCta}
            data-testid="before-after-cta"
            className="group px-7 py-3.5 rounded-full btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-2xl shadow-violet-500/40 flex items-center gap-2"
          >
            Start Your Online Journey
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
